import React from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiUsers, FiActivity, FiCheckCircle, FiClock } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const DashboardView = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };
  
  return (
    <Container maxW="container.xl" py={10}>
      <Flex direction="column" align="stretch">
        <Flex justify="space-between" align="center" mb={8}>
          <Box>
            <Heading as="h1" size="xl" color={textColor}>
              Dashboard
            </Heading>
            <Text mt={2} color={textColor}>
              Welcome back, {currentUser?.name || currentUser?.email || 'User'}!
            </Text>
          </Box>
          <Button colorScheme="teal" variant="outline" onClick={handleLogout}>
            Sign Out
          </Button>
        </Flex>
        
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
          <StatCard
            icon={FiUsers}
            title="Users"
            value="45"
            change="+5%"
            description="From last week"
            color="blue.500"
          />
          <StatCard
            icon={FiActivity}
            title="Activity"
            value="82%"
            change="+12%"
            description="From last month"
            color="green.500"
          />
          <StatCard
            icon={FiCheckCircle}
            title="Tasks"
            value="24"
            change="Completed"
            description="This week"
            color="purple.500"
          />
          <StatCard
            icon={FiClock}
            title="Hours"
            value="32.5"
            change="Tracked"
            description="This week"
            color="orange.500"
          />
        </SimpleGrid>
        
        <Box
          bg={cardBg}
          p={6}
          borderRadius="lg"
          boxShadow="md"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Heading as="h2" size="md" mb={4} color={textColor}>
            Recent Activity
          </Heading>
          <Text color={textColor}>
            This is a protected dashboard page. You can only see this if you're logged in.
            In a real application, this would display user-specific data and analytics.
          </Text>
        </Box>
      </Flex>
    </Container>
  );
};

// Stat Card Component
const StatCard = ({ icon, title, value, change, description, color }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  return (
    <Box
      bg={cardBg}
      p={6}
      borderRadius="lg"
      boxShadow="md"
      borderWidth="1px"
      borderColor={borderColor}
    >
      <Flex justify="space-between" align="center">
        <Box>
          <Stat>
            <StatLabel color={textColor}>{title}</StatLabel>
            <StatNumber fontSize="2xl" color={textColor}>{value}</StatNumber>
            <StatHelpText>
              <Text as="span" color={color}>{change}</Text>
              {description && <Text as="span" ml={1} color={textColor}>{description}</Text>}
            </StatHelpText>
          </Stat>
        </Box>
        <Icon as={icon} boxSize="10" color={color} />
      </Flex>
    </Box>
  );
};

export default DashboardView;
